import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import logService from '../services/logService';
import useEscapeClose from '../hooks/useEscapeClose';
import { API_URL } from '../config/api';
import './Login.css';

const RECAPTCHA_SITE_KEY = '6Lc4v40sAAAAAINy3CAuY-TUzC2Uz001QT1xOu9I';

const Login = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  // Sayfa yüklendiğinde kayıtlı email'i kontrol et
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  // QR Scanner state
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrError, setQrError] = useState('');
  const [qrScanning, setQrScanning] = useState(false);
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);

  useEscapeClose([
    [showQrScanner, () => setShowQrScanner(false)],
    [requires2FA, () => setRequires2FA(false)],
  ]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // reCAPTCHA v3 token al
      let recaptchaToken = '';
      if (window.grecaptcha) {
        try {
          recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'login' });
        } catch (recaptchaError) {
          console.error('reCAPTCHA hatası:', recaptchaError);
        }
      }

      const loginData = { email, password, rememberMe, recaptchaToken };
      if (requires2FA && totpCode) {
        loginData.totpCode = totpCode;
      }

      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(loginData)
      });

      const data = await response.json();

      if (data.requires2FA) {
        // 2FA kodu gerekli
        setRequires2FA(true);
        setLoading(false);
        return;
      }

      if (data.success) {
        // Beni Hatırla - email kaydet/sil
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        // Token ve kullanıcı bilgilerini kaydet
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Parent component'e bildir
        onLogin(data.user);

        // Rol'e göre yönlendir
        if (data.user.role === 'terminal') {
          navigate('/terminal');
        } else if (data.user.role === 'atolye') {
          navigate('/workshop');
        } else {
          navigate('/');
        }
      } else {
        setError(data.message || 'Giriş başarısız!');
        logService.loginFailed(email, data.message || 'Geçersiz kimlik bilgileri');
      }
    } catch (err) {
      console.error('Login hatası:', err);
      setError('Sunucuya bağlanılamadı!');
      logService.loginFailed(email, 'Sunucu bağlantı hatası');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setTotpCode('');
    setError('');
  };

  // QR kodu ile giriş
  const handleQrLogin = async (token) => {
    setQrScanning(true);
    setQrError('');

    try {
      const response = await fetch(`${API_URL}/auth/qr-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (data.success) {
        // Scanner'ı kapat
        stopQrScanner();
        setShowQrScanner(false);

        // Token ve kullanıcı bilgilerini kaydet
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));

        // Parent component'e bildir
        onLogin(data.user);

        // Terminal sayfasına yönlendir
        navigate('/terminal');
      } else {
        setQrError(data.message || 'QR kodu ile giriş başarısız!');
        // Taramaya devam et
        setQrScanning(false);
      }
    } catch (error) {
      console.error('QR login hatası:', error);
      setQrError('Sunucuya bağlanılamadı!');
      setQrScanning(false);
    }
  };

  // QR Scanner'ı başlat
  const startQrScanner = async () => {
    if (html5QrCodeRef.current) return;

    try {
      const html5QrCode = new Html5Qrcode("qr-scanner-container");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1
        },
        (decodedText) => {
          // QR kodu okundu
          handleQrLogin(decodedText);
        },
        () => {} // Hata callback (sessiz)
      );
    } catch (err) {
      console.error('Kamera başlatılamadı:', err);
      setQrError('Kamera erişimi sağlanamadı. Lütfen kamera izinlerini kontrol edin.');
    }
  };

  // QR Scanner'ı durdur
  const stopQrScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        console.error('Scanner durdurma hatası:', err);
      }
    }
  };

  // QR Scanner modalını aç
  const openQrScanner = () => {
    setShowQrScanner(true);
    setQrError('');
    setQrScanning(false);
    // Kısa bir gecikme ile kamerayı başlat (DOM render için)
    setTimeout(() => {
      startQrScanner();
    }, 100);
  };

  // QR Scanner modalını kapat
  const closeQrScanner = () => {
    stopQrScanner();
    setShowQrScanner(false);
    setQrError('');
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopQrScanner();
    };
  }, []);

  return (
    <div className="login-container">
      {/* Sol Taraf - Video */}
      <div className="login-left">
        <video
          className="login-video-bg"
          autoPlay
          loop
          muted
          playsInline
        >
          <source src="/login-bg.mp4" type="video/mp4" />
        </video>
        <div className="login-overlay"></div>
        <div className="login-brand">
          <img src="/logo.png" alt="Akik Gümüş" className="brand-logo" />
        </div>
      </div>

      {/* Sağ Taraf - Login Formu */}
      <div className="login-right">
        <div className="login-card">
        <div className="login-header">
          <div className="logo-container">
            <img src="/logo.png" alt="Akik Gümüş" className="login-logo" />
          </div>
          <p>Yönetim Paneline Hoş Geldiniz</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && (
            <div className="error-message">
              <span className="error-icon">!</span>
              {error}
            </div>
          )}

          {!requires2FA ? (
            <>
              <div className="form-group">
                <label htmlFor="email">E-posta Adresi</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                  </span>
                  <input
                    type="email"
                    id="email"
                    placeholder="ornek@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="password">Şifre</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                  <input
                    type="password"
                    id="password"
                    placeholder="Şifrenizi girin"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="remember-me-group">
                <label className="remember-me-label">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="remember-me-checkbox"
                  />
                  <span className="remember-me-checkmark"></span>
                  <span className="remember-me-text">Beni Hatırla</span>
                </label>
              </div>
            </>
          ) : (
            <>
              <div className="two-factor-info">
                <div className="two-factor-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                    <path d="M9 12l2 2 4-4"/>
                  </svg>
                </div>
                <h3>İki Faktörlü Doğrulama</h3>
                <p>Authenticator uygulamanızdaki 6 haneli kodu girin</p>
              </div>

              <div className="form-group">
                <label htmlFor="totpCode">Doğrulama Kodu</label>
                <div className="input-wrapper">
                  <span className="input-icon">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                  </span>
                  <input
                    type="text"
                    id="totpCode"
                    placeholder="000000"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    maxLength={6}
                    pattern="\d{6}"
                    autoComplete="one-time-code"
                    inputMode="numeric"
                  />
                </div>
              </div>

              <button
                type="button"
                className="back-btn"
                onClick={handleBack}
              >
                ← Geri Dön
              </button>
            </>
          )}

          <button
            type="submit"
            className={`login-btn ${loading ? 'loading' : ''}`}
            disabled={loading || (requires2FA && totpCode.length !== 6)}
          >
            {requires2FA ? 'Doğrula' : 'Giriş Yap'}
          </button>

          {/* QR Kodu ile Giriş Butonu */}
          {!requires2FA && (
            <button
              type="button"
              className="qr-login-btn"
              onClick={openQrScanner}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
              QR Kodu ile Giriş
            </button>
          )}
          </form>
        </div>

        <footer className="login-footer-bottom">
          <p>© 2025 <a href="https://www.qubite.net/" target="_blank" rel="noopener noreferrer">Qubite ERP ve Perakende Çözümleri</a> ile geliştirilmiştir</p>
        </footer>
      </div>

      {/* QR Scanner Modal */}
      {showQrScanner && (
        <div className="qr-scanner-overlay" onClick={closeQrScanner}>
          <div className="qr-scanner-modal" onClick={e => e.stopPropagation()}>
            <div className="qr-scanner-header">
              <h3>QR Kodu ile Giriş</h3>
              <button className="qr-close-btn" onClick={closeQrScanner}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>
            <div className="qr-scanner-body">
              <p className="qr-scanner-info">El terminali QR kodunuzu taratın</p>

              {qrError && (
                <div className="qr-error-message">
                  <span className="error-icon">!</span>
                  {qrError}
                </div>
              )}

              <div
                id="qr-scanner-container"
                ref={scannerContainerRef}
                className="qr-scanner-container"
              />

              {qrScanning && (
                <div className="qr-scanning-indicator">
                  <div className="qr-spinner"></div>
                  <span>Giriş yapılıyor...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
